import React from "react"
import { Info } from "lucide-react"
import { Label } from "../../../ui/label"
import { Input as ShadcnInput } from "../../../ui/input"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip"
import { cn } from "../../../lib/utils"
import FileInput from "./FileInput"

/**
 * Input component - Migrated to use shadcn/ui Input and Label
 * 
 * @param {string} title - Input label
 * @param {string} type - Input type
 * @param {string} name - Input name
 * @param {string} value - Input value
 * @param {string} placeholder - Placeholder text
 * @param {function} setValue - Change handler
 * @param {RegExp} regex - Validation regex
 * @param {number} maxLen - Max length
 * @param {string} errorMsg - Error message
 * @param {boolean} disabled - Disabled state
 * @param {string} maxValue - Max value for number inputs
 * @param {string} accept - Accept attribute for file inputs
 * @param {boolean} inline - Inline layout
 * @param {object} inlineStyle - Custom inline layout classes
 * @param {string} defaultValue - Default value
 * @param {string} ids - Input ID
 * @param {function} onFocus - Focus handler
 * @param {boolean} isTofocus - Auto focus flag
 * @param {object} style - Custom styles
 * @param {string} info - Tooltip info text
 * @param {string} className - Additional classes
 * @param {boolean} show - Show tooltip
 */
// A field carrying an error already renders its message in red underneath;
// the control itself is outlined to match, so the field that needs attention
// is visible at a glance and not only once the message is read (INT-10948).
const INVALID_FIELD_CLASS =
  "border-destructive focus-visible:ring-destructive focus:ring-destructive"

const sanitizeValue = (v) => {
  if (v === null || v === undefined) return ""
  if (typeof v === "number" && isNaN(v)) return ""
  return v
}

const Input = (props) => {
  const {
    title,
    type,
    name,
    value: rawValue,
    placeholder,
    setValue,
    regex,
    maxLen,
    errorMsg,
    disabled,
    maxValue,
    accept,
    inline,
    inlineStyle,
    defaultValue,
    ids,
    onFocus,
    isTofocus,
    style,
    info,
    className,
    show = true,
    setKey,
    ...rest
  } = props

  const value = sanitizeValue(rawValue)

  const inputRef = React.useRef(null)

  React.useEffect(() => {
    if (isTofocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isTofocus])

  const handleChange = (e) => {
    const { value } = e.target
    if (value === "" || (regex !== undefined ? regex.test(value) : true)) {
      setValue(e)
    }
  }

  // Delegate to FileInput for file uploads
  if (type === "file") {
    return (
      <FileInput
        title={title}
        ids={ids}
        name={name}
        accept={accept}
        onChange={setValue}
        errorMsg={errorMsg}
        disabled={disabled}
        info={info}
        show={show}
        className={className}
        placeholder={placeholder || "No file chosen"}
      />
    )
  }

  // Inline layout
  if (inline) {
    return (
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className={cn("col-span-6", inlineStyle?.label)}>
          <Label htmlFor={ids} className="flex items-center gap-2">
            {title}
            {show && info && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 cursor-help text-muted-foreground " />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{info}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Label>
        </div>
        <div className={cn("col-span-6", inlineStyle?.input)}>
          <ShadcnInput
            ref={inputRef}
            type={type}
            id={ids}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            autoComplete="off"
            maxLength={maxLen}
            disabled={disabled}
            accept={accept}
            max={maxValue}
            defaultValue={defaultValue}
            readOnly={type === "label"}
            onFocus={onFocus}
            onKeyDown={setKey}
            aria-invalid={errorMsg ? true : undefined}
            className={cn(errorMsg && INVALID_FIELD_CLASS, className)}
            style={style}
            {...rest}
          />
        </div>
      </div>
    )
  }

  // Default vertical layout
  return (
    <div className="space-y-2">
      {title && (
        <Label htmlFor={ids} className="flex items-center gap-2">
          {title}
          {show && info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{info}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Label>
      )}
      <ShadcnInput
        ref={inputRef}
        type={type}
        id={ids}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        autoComplete="off"
        maxLength={maxLen}
        disabled={disabled}
        accept={accept}
        max={maxValue}
        defaultValue={defaultValue}
        readOnly={type === "label"}
        onFocus={onFocus}
        onKeyDown={setKey}
        aria-invalid={errorMsg ? true : undefined}
        className={cn(errorMsg && INVALID_FIELD_CLASS, className)}
        style={style}
        {...rest}
      />
      {errorMsg && (
        <p className="text-sm text-destructive">{errorMsg}</p>
      )}
    </div>
  )
}

export default Input
